import axios from 'axios';

import { Id, PagedResponse, getData } from '@feature/utils';

import {
  TravelActivityType,
  TravelCancelRequest,
  TravelDayBalancePeopleExportRequest,
  TravelDayBalancePeoplePage,
  TravelDayBalancePeopleParams,
  TravelDayBalancePreview,
  TravelDayBalancePreviewParams,
  TravelDocumentKinds,
  TravelFileUploadRequest,
  TravelOverlap,
  TravelOverlapRequest,
  TravelOwnDayBalance,
  TravelPaperTitleCheck,
  TravelPaperTitleCheckParams,
  TravelRequest,
  TravelRequestFile,
  TravelRequestParams,
  TravelRequestRequest,
  TravelRequestSummary,
} from './TravelRequestTypes';

export function getPaged(params: TravelRequestParams) {
  return axios
    .get<PagedResponse<TravelRequestSummary>>('/travel/requests/paged', {
      params,
    })
    .then(getData);
}

export function get(id: Id) {
  return axios.get<TravelRequest>(`/travel/requests/${id}`).then(getData);
}

export function getOverlaps(id: Id) {
  return axios.get<TravelOverlap[]>(`/travel/requests/${id}/overlaps`).then(getData);
}

export function checkPaperTitle(params: TravelPaperTitleCheckParams) {
  return axios.get<TravelPaperTitleCheck>('/travel/requests/paper-title-check', { params }).then(getData);
}

export function getUnfinishedErasmus() {
  return axios.get<TravelOverlapRequest[]>('/travel/requests/unfinished-erasmus').then(getData);
}

export function getUnfinishedReturns() {
  return axios.get<TravelOverlapRequest[]>('/travel/requests/unfinished-returns').then(getData);
}

export function create(request: TravelRequestRequest) {
  return axios.post<TravelRequest>('/travel/requests', request).then(getData);
}

export function edit(id: Id, request: TravelRequestRequest) {
  return axios.put<TravelRequest>(`/travel/requests/${id}`, request).then(getData);
}

export function deleteById(id: Id) {
  return axios.delete<unknown>(`/travel/requests/${id}`).then(getData);
}

export function submit(id: Id) {
  return axios.post<TravelRequest>(`/travel/requests/${id}/submit`).then(getData);
}

export function cancel(id: Id, request: TravelCancelRequest) {
  return axios.post<TravelRequest>(`/travel/requests/${id}/cancel`, request).then(getData);
}

export function getDayBalancePreview(params: TravelDayBalancePreviewParams) {
  return axios.get<TravelDayBalancePreview>('/travel/requests/day-balance/preview', { params }).then(getData);
}

export function getOwnDayBalance(year: number, userId?: Id | null) {
  return axios.get<TravelOwnDayBalance>('/travel/day-balance', { params: { year, userId } }).then(getData);
}

export function getDayBalancePeople(params: TravelDayBalancePeopleParams) {
  return axios.get<TravelDayBalancePeoplePage>('/travel/day-balance/people', { params }).then(getData);
}

export function exportDayBalancePeople(params: TravelDayBalancePeopleExportRequest) {
  return axios.get<Blob>('/travel/day-balance/people/export', { responseType: 'blob', params });
}

export function complete(id: Id) {
  return axios.post<TravelRequest>(`/travel/requests/${id}/complete`).then(getData);
}

export function addFile(id: Id, request: TravelFileUploadRequest) {
  const formData = new FormData();
  formData.append('kind', request.kind);
  formData.append('file', request.file);

  return axios
    .post<TravelRequestFile>(`/travel/requests/${id}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function deleteFile(id: Id, fileId: Id) {
  return axios.delete<unknown>(`/travel/requests/${id}/files/${fileId}`).then(getData);
}

export function downloadFile(id: Id, fileId: Id) {
  return axios.get<Blob>(`/travel/requests/${id}/files/${fileId}/download`, { responseType: 'blob' });
}

export function downloadDocument(id: Id) {
  return axios.get<Blob>(`/travel/requests/${id}/document`, { responseType: 'blob' });
}

export function downloadTravelOrder(id: Id) {
  return axios.get<Blob>(`/travel/requests/${id}/travel-order`, { responseType: 'blob' });
}

export function getDocumentKinds(activityType: TravelActivityType, withMobilitySupport: boolean) {
  return axios
    .get<TravelDocumentKinds>('/travel/requests/document-kinds', {
      params: { activityType, withMobilitySupport },
    })
    .then(getData);
}
